import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useController, useFormContext } from "react-hook-form";
import IconButton from "../../../Buttons/IconButton";
import Button from "../../../Buttons/Button";
import NumberField from "../../../Form/NumberField";
import { useState, useEffect } from "react";
import {
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_DAYS,
  PRODUCT_SET_AUDIENCE_BUILDER_TITLE,
  PRODUCT_SET_AUDIENCE_BUILDER_PROSPECTING_TITLE,
  PRODUCT_SET_AUDIENCE_BUILDER_RETARGETING_TITLE,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_TITLE,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_INCLUSIONS_ADD_BUTTON,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_EXCLUSIONS_TITLE,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_EXCLUSIONS_ADD_BUTTON,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_IN_THE_LAST,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_INCLUSIONS_TITLE,
  PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_SELECT_PRODUCT_SET_LABEL,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import SelectProductsSetMeta from "../Campaign/SelectProductsSetMeta";
import { META } from "../../../../utils/constants";
import _ from "lodash";
import { detectPreset, PRESETS } from "../../../../services/meta";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";

// Constants
const RETARGET_CONFIGURATION_KEY = "retarget";
const PROSPECTING_CONFIGURATION_KEY = "prospecting";

const EVENT_OPTIONS = [
  { value: "ViewContent", label: "Viewed" },
  { value: "AddToCart", label: "Added to cart" },
  { value: "Purchase", label: "Purchased" },
];

const defaultCustom = {
  inclusions: [
    { retentionSeconds: "1209600", rule: '{"event":{"eq":"ViewContent"}}' },
    { retentionSeconds: "1209600", rule: '{"event":{"eq":"AddToCart"}}' },
  ],
  exclusions: [
    { retentionSeconds: "1209600", rule: '{"event":{"eq":"Purchase"}}' },
  ],
  productSetID: null,
};

const commonSxTextField = {
  width: 50,
  "& .MuiInputBase-input": {
    py: 0,
  },
};
// Utility functions
const daysToSeconds = (days) => (days * 86400).toString();
const secondsToDays = (seconds) => Math.floor(parseInt(seconds) / 86400);
const createEventRule = (event) => `{"event":{"eq":"${event}"}}`;
const parseEventFromRule = (rule) => {
  const match = rule.match(/"eq":"([^"]+)"/);
  return match ? match[1] : "ViewContent";
};

const validateDays = (days) => {
  const numDays = Number(days);
  if (isNaN(numDays)) return 0;
  if (numDays < 0) return 0;
  if (numDays > 180) return 180;
  return numDays;
};

const getAvailableEvents = (currentEvents) => {
  const usedEvents = currentEvents.map((event) =>
    parseEventFromRule(event.rule)
  );
  return EVENT_OPTIONS.filter((opt) => !usedEvents.includes(opt.value));
};

const getFirstAvailableEvent = (currentEvents) => {
  const available = getAvailableEvents(currentEvents);
  return available.length > 0 ? available[0].value : null;
};

// Validation functions
const isEventDuplicate = (event, list, currentIndex) => {
  return list.some(
    (item, idx) => idx !== currentIndex && item.rule === createEventRule(event)
  );
};

const hasEventWithSameDays = (event, days, list) => {
  return list.some(
    (item) =>
      item.rule === createEventRule(event) && item.retentionSeconds === days
  );
};

const ProductSetAudienceBuilderMeta = ({
  name,
  control,
  productSetSelected,
  catalogID,
  integrationID,
  adAccountID,
  errors,
  sxTextField,
}) => {
  const { t } = useTranslationApp();
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [targetingType, setTargetingType] = useState(
    RETARGET_CONFIGURATION_KEY
  );

  const { setValue } = useFormContext();
  const {
    field: { value: valueField, onChange: onChangeField },
  } = useController({ control, name });

  // Set initial preset on first render
  useEffect(() => {
    const isEmptyField =
      !valueField ||
      (valueField?.inclusions?.length === 0 &&
        valueField?.exclusions?.length === 0);

    if (!selectedPreset && !isEmptyField) {
      const detectedPreset = detectPreset({
        productSetAudience: valueField,
        productSetID: productSetSelected?.id || productSetSelected,
      });
      setSelectedPreset(detectedPreset);
    }
    if (!selectedPreset && isEmptyField) {
      onTargetingTypeChange(
        {
          target: { value: PROSPECTING_CONFIGURATION_KEY },
        },
        "initialize"
      );
    } else {
      const formattedObject = {
        productSetID: valueField?.productSetID || "",
        inclusions: _.isEmpty(valueField?.inclusions)
          ? []
          : valueField?.inclusions,
        exclusions: _.isEmpty(valueField?.exclusions)
          ? []
          : valueField?.exclusions,
      };
      setValue(name, formattedObject, { shouldDirty: false });
    }

    // eslint-disable-next-line
  }, []);

  const onTargetingTypeChange = (event, from) => {
    const newType = event.target.value;
    setTargetingType(newType);

    if (newType === PROSPECTING_CONFIGURATION_KEY) {
      const newValue = { inclusions: [], exclusions: [] };
      if (from === "initialize") {
        setValue(name, newValue, { shouldDirty: false });
      } else {
        onChangeField(newValue);
      }
      setSelectedPreset(null);
    } else {
      const firstPreset = PRESETS[0];
      onPresetChange({ target: { value: firstPreset.key } }, from);
    }
  };

  const onPresetChange = (e, from) => {
    const newPreset = e.target.value;
    setSelectedPreset(newPreset);
    if (newPreset !== "custom") {
      const selectedPreset = _.cloneDeep(
        PRESETS.find((p) => p.key === newPreset)
      );
      const structure = selectedPreset?.structure || defaultCustom;

      if (
        newPreset === "viewedOrAddedNotPurchased" ||
        newPreset === "addedNotPurchased"
      ) {
        structure.productSetID =
          productSetSelected?.id || productSetSelected || null;
      }

      if (from === "initialize") {
        setValue(name, structure, { shouldDirty: false });
      } else {
        onChangeField(structure);
      }
    }
  };
  const onPresetDaysChange = (presetKey, newDays) => {
    const preset = _.cloneDeep(PRESETS.find((p) => p.key === presetKey));
    if (!preset) return;

    const validatedDays = validateDays(newDays);

    // Create new structure with updated days
    const newStructure = {
      ...valueField,
      inclusions: preset.structure.inclusions.map((inc) => ({
        ...inc,
        retentionSeconds: daysToSeconds(validatedDays),
      })),
      exclusions:
        preset.structure.exclusions?.map((exc) => ({
          ...exc,
          retentionSeconds: daysToSeconds(validatedDays),
        })) || [],
    };

    onChangeField(newStructure);
  };

  const onCustomInclusionChange = (idx, key, val) => {
    if (!valueField) return;

    // Validate limits and duplicates
    if (key === "rule") {
      if (valueField.inclusions.length >= 3 && !valueField.inclusions[idx]) {
        return;
      }
      if (isEventDuplicate(val, valueField.inclusions, idx)) {
        return;
      }
      if (
        hasEventWithSameDays(
          val,
          valueField.inclusions[idx].retentionSeconds,
          valueField.exclusions
        )
      ) {
        return;
      }
    }

    const inclusions = valueField.inclusions.map((inc, i) =>
      i === idx
        ? {
          ...inc,
          [key]:
            key === "retentionSeconds"
              ? daysToSeconds(validateDays(val))
              : createEventRule(val),
        }
        : inc
    );

    onChangeField({ ...valueField, inclusions });
  };

  const onCustomExclusionChange = (idx, key, val) => {
    if (!valueField) return;

    // Validate limits and duplicates
    if (key === "rule") {
      if (valueField.exclusions.length >= 3 && !valueField.exclusions[idx]) {
        return;
      }
      if (isEventDuplicate(val, valueField.exclusions, idx)) {
        return;
      }
      if (
        hasEventWithSameDays(
          val,
          valueField.exclusions[idx].retentionSeconds,
          valueField.inclusions
        )
      ) {
        return;
      }
    }

    const exclusions = valueField.exclusions.map((exc, i) =>
      i === idx
        ? {
          ...exc,
          [key]:
            key === "retentionSeconds"
              ? daysToSeconds(validateDays(val))
              : createEventRule(val),
        }
        : exc
    );

    onChangeField({ ...valueField, exclusions });
  };

  const onAddInclusion = () => {
    if (!valueField || valueField.inclusions.length >= 3) return;

    const newEvent = getFirstAvailableEvent(valueField.inclusions);
    if (!newEvent) return;

    const newDays = "604800";

    if (hasEventWithSameDays(newEvent, newDays, valueField.inclusions)) {
      return;
    }

    onChangeField({
      ...valueField,
      inclusions: [
        ...valueField.inclusions,
        {
          retentionSeconds: newDays,
          rule: createEventRule(newEvent),
        },
      ],
    });
  };

  const onRemoveInclusion = (idx) => {
    if (!valueField) return;

    onChangeField({
      ...valueField,
      inclusions: valueField.inclusions.filter((_, i) => i !== idx),
    });
  };

  const onAddExclusion = () => {
    if (!valueField || valueField.exclusions.length >= 3) return;

    const newEvent = getFirstAvailableEvent(valueField.exclusions);
    if (!newEvent) return;

    const newDays = "604800";

    if (hasEventWithSameDays(newEvent, newDays, valueField.exclusions)) {
      return;
    }

    onChangeField({
      ...valueField,
      exclusions: [
        ...valueField.exclusions,
        {
          retentionSeconds: newDays,
          rule: createEventRule(newEvent),
        },
      ],
    });
  };

  const onRemoveExclusion = (idx) => {
    if (!valueField) return;

    onChangeField({
      ...valueField,
      exclusions: valueField.exclusions.filter((_, i) => i !== idx),
    });
  };

  const onProductSetChange = (value) => {
    onChangeField({
      ...valueField,
      productSetID: value?.id,
    });
  };

  // Render functions
  const renderPreset = (preset) => {
    const isSelected = selectedPreset === preset.key;
    const currentDays =
      isSelected && valueField
        ? secondsToDays(valueField.inclusions[0].retentionSeconds)
        : secondsToDays(preset.structure.inclusions[0].retentionSeconds);

    return (
      <Box key={preset.key} sx={{ mb: 2 }}>
        <FormControlLabel
          value={preset.key}
          control={<Radio sx={{ pt: 0.25 }} />}
          sx={{
            alignItems: "flex-start",
          }}
          label={
            <Box>
              <Typography variant="subtitle1">{t(preset.labelKey)}</Typography>
              <Typography
                variant="div"
                color="fields.placeholder"
                sx={{
                  lineHeight: 2,
                }}
              >
                {t(preset.descriptionKey, {
                  value: productSetSelected?.name || "",
                })}
                <FormControl>
                  <TextField
                    hiddenLabel
                    variant="filled"
                    size="small"
                    InputProps={{
                      inputComponent: NumberField,
                      inputProps: { min: 0, max: 180 },
                    }}
                    value={currentDays}
                    sx={{ ...commonSxTextField, mx: 1, border: `1px solid ${INPUT_COLOR_BORDER}` }}
                    onChange={(e) =>
                      onPresetDaysChange(preset.key, Number(e.target.value))
                    }
                    disabled={!isSelected}
                  />
                </FormControl>
                {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_DAYS)}
              </Typography>
              {isSelected &&
                (preset.key === "upsell" || preset.key === "crossSell") && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      color="fields.placeholder"
                      gutterBottom
                    >
                      {t(preset.productSetLabelKey)}
                    </Typography>
                    <SelectProductsSetMeta
                      fullWidth
                      variant="filled"
                      platform={META}
                      integrationID={integrationID}
                      adAccountID={adAccountID}
                      onChangeAction={onProductSetChange}
                      catalogID={catalogID}
                      control={control}
                      name={`${name}.productSetID`}
                      errors={errors}
                      sxTextField={sxTextField}
                    />
                  </Box>
                )}
            </Box>
          }
        />
      </Box>
    );
  };

  const renderCustomInclusion = (inc, idx) => {
    const event = parseEventFromRule(inc.rule);
    const days = secondsToDays(inc.retentionSeconds);
    const availableEvents = getAvailableEvents(valueField?.inclusions || []);

    const lengthRules =
      (valueField?.inclusions?.length || 0) +
      (valueField?.exclusions?.length || 0);
    const showRemove = lengthRules >= 2 && valueField?.inclusions?.length >= 2;

    return (
      <Box
        key={idx}
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 1,
          mb: 1,
        }}
      >
        <FormControl size="small" sx={{ mr: 1, width: 150 }}>
          <Select
            value={event}
            onChange={(e) =>
              onCustomInclusionChange(idx, "rule", e.target.value)
            }
            sx={{
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              ...sxTextField,
            }}
          >
            {EVENT_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                disabled={
                  !availableEvents.find((e) => e.value === opt.value) &&
                  opt.value !== event
                }
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_IN_THE_LAST)}
        </Typography>
        <FormControl>
          <TextField
            hiddenLabel
            variant="filled"
            size="small"
            InputProps={{
              inputComponent: NumberField,
              inputProps: { min: 0, max: 180 },
            }}
            value={days}
            sx={{ ...commonSxTextField, mr: 1, border: `1px solid ${INPUT_COLOR_BORDER}` }}
            onChange={(e) =>
              onCustomInclusionChange(
                idx,
                "retentionSeconds",
                Number(e.target.value)
              )
            }
          />
        </FormControl>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_DAYS)}
        </Typography>
        {showRemove && (
          <IconButton size="small" onClick={() => onRemoveInclusion(idx)}>
            <RemoveCircleOutlineIcon />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderCustomExclusion = (exc, idx) => {
    const event = parseEventFromRule(exc.rule);
    const days = secondsToDays(exc.retentionSeconds);
    const availableEvents = getAvailableEvents(valueField?.exclusions || []);

    const lengthRules =
      (valueField?.inclusions?.length || 0) +
      (valueField?.exclusions?.length || 0);
    const showRemove = lengthRules >= 2;

    return (
      <Box
        key={idx}
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1,
        }}
      >
        <FormControl size="small" sx={{ mr: 1, width: 150 }}>
          <Select
            value={event}
            onChange={(e) =>
              onCustomExclusionChange(idx, "rule", e.target.value)
            }
            sx={{
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              ...sxTextField,
            }}
          >
            {EVENT_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                disabled={
                  !availableEvents.find((e) => e.value === opt.value) &&
                  opt.value !== event
                }
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_IN_THE_LAST)}
        </Typography>
        <FormControl>
          <TextField
            hiddenLabel
            variant="filled"
            size="small"
            InputProps={{
              inputComponent: NumberField,
              inputProps: { min: 0, max: 180 },
            }}
            value={days}
            sx={{ ...commonSxTextField, mr: 1, border: `1px solid ${INPUT_COLOR_BORDER}` }}
            onChange={(e) =>
              onCustomExclusionChange(
                idx,
                "retentionSeconds",
                Number(e.target.value)
              )
            }
          />
        </FormControl>
        <Typography variant="body2" sx={{ mr: 1 }}>
          {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_DAYS)}
        </Typography>
        {showRemove && (
          <IconButton size="small" onClick={() => onRemoveExclusion(idx)}>
            <RemoveCircleOutlineIcon />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <FormControl fullWidth component="fieldset">
        <FormLabel
          component="legend"
          sx={{
            mb: 1,
          }}
        >
          {t(PRODUCT_SET_AUDIENCE_BUILDER_TITLE)}
        </FormLabel>
        <RadioGroup
          value={targetingType}
          onChange={onTargetingTypeChange}
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            flexWrap: "nowrap",
          }}
        >
          <FormControlLabel
            value={PROSPECTING_CONFIGURATION_KEY}
            control={<Radio sx={{ pt: 0.25 }} />}
            sx={{
              alignItems: "flex-start",
            }}
            label={
              <Box>
                <Typography variant="subtitle1">
                  {t(PRODUCT_SET_AUDIENCE_BUILDER_PROSPECTING_TITLE)}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value={RETARGET_CONFIGURATION_KEY}
            control={<Radio sx={{ pt: 0.25 }} />}
            sx={{
              alignItems: "flex-start",
            }}
            label={
              <Box>
                <Typography variant="subtitle1">
                  {t(PRODUCT_SET_AUDIENCE_BUILDER_RETARGETING_TITLE)}
                </Typography>
              </Box>
            }
          />
        </RadioGroup>

        <Divider sx={{ my: 2 }} />

        {targetingType === RETARGET_CONFIGURATION_KEY && (
          <RadioGroup value={selectedPreset} onChange={onPresetChange}>
            {PRESETS.map(renderPreset)}
            <Divider sx={{ my: 2 }} />
            <Box>
              <FormControlLabel
                value="custom"
                control={<Radio sx={{ pt: 0.25 }} />}
                sx={{
                  alignItems: "flex-start",
                }}
                label={
                  <Typography variant="subtitle1">
                    {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_TITLE)}
                  </Typography>
                }
              />
              {selectedPreset === "custom" && valueField && (
                <Box sx={{ pl: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    {t(
                      PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_SELECT_PRODUCT_SET_LABEL
                    )}
                  </Typography>
                  <SelectProductsSetMeta
                    fullWidth
                    variant="filled"
                    platform={META}
                    integrationID={integrationID}
                    adAccountID={adAccountID}
                    catalogID={catalogID}
                    control={control}
                    name={`${name}.productSetID`}
                    sxTextField={sxTextField}
                    errors={errors}
                  />
                  <Typography variant="body2" sx={{ my: 1 }}>
                    {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_INCLUSIONS_TITLE)}
                  </Typography>
                  {valueField.inclusions.map(renderCustomInclusion)}
                  {valueField.inclusions.length < 3 && (
                    <Button
                      color="secondary"
                      startIcon={<AddIcon />}
                      size="small"
                      sx={{ mb: 2, fontSize: 14 }}
                      onClick={onAddInclusion}
                    >
                      {t(
                        PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_INCLUSIONS_ADD_BUTTON
                      )}
                    </Button>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t(PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_EXCLUSIONS_TITLE)}
                  </Typography>
                  {valueField.exclusions.map(renderCustomExclusion)}
                  {valueField.exclusions.length < 3 && (
                    <Button
                      color="secondary"
                      startIcon={<AddIcon />}
                      size="small"
                      sx={{ fontSize: 14 }}
                      onClick={onAddExclusion}
                    >
                      {t(
                        PRODUCT_SET_AUDIENCE_BUILDER_CUSTOM_EXCLUSIONS_ADD_BUTTON
                      )}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </RadioGroup>
        )}
      </FormControl>
    </Box>
  );
};

export default ProductSetAudienceBuilderMeta;
